// Farbige Datei-Typ-Icons für den Dokumente-Explorer.
//
// BEWUSST NICHT in redesign/kvpIcons.tsx: das Set dort ist durchgehend
// monochrom (`stroke="currentColor"`) und wird an vielen Stellen eingefärbt
// vom Elternkontext geerbt — feste Farben würden dort ausbrechen. Hier ist die
// Farbe dagegen der Punkt: ein Explorer erkennt man an rotem PDF, blauem Word,
// grünem Excel und gelbem Ordner.
//
// Basis sind die `*24Filled`-Varianten aus @fluentui/react-icons (die einzige
// Icon-Bibliothek im Repo); gefüllte Formen tragen Farbe deutlich besser als
// die Outline-Variante.

import * as React from "react";
import {
  Document24Filled,
  DocumentPdf24Filled,
  DocumentTable24Filled,
  DocumentText24Filled,
  Folder24Filled,
  FolderZip24Filled,
  Image24Filled,
  Mail24Filled,
  MusicNote224Filled,
  SlideText24Filled,
  VideoClip24Filled,
} from "@fluentui/react-icons";
import { extOf } from "../../../utils/fileUtils";
import { FILE_TYPE_COLORS as C, KIND_LABEL, fileKind, type FileKindKey } from "../dateiTyp";

// Einordnung, Namen und Farben liegen seit Oktober 2026 in `../dateiTyp.ts`
// (gemeinsam mit dem Vorgang); hier bleiben die Fluent-Icons des Explorers.
export { fileKind, type FileKindKey };

/**
 * Label für die Typ-Spalte: die Extension in Großbuchstaben, wenn vorhanden
 * (das ist die Information, die im Explorer wirklich hilft), sonst der
 * fachliche Typname.
 */
export const fileTypeLabel = (fileName: string, mimeType?: string | null): string => {
  const e = extOf(fileName);
  return e ? e.toUpperCase() : KIND_LABEL[fileKind(fileName, mimeType)];
};

/** Ausführlicher Typname für den Tooltip. */
export const fileKindLabel = (fileName: string, mimeType?: string | null): string =>
  KIND_LABEL[fileKind(fileName, mimeType)];

const ICON_BY_KIND: Record<FileKindKey, { Comp: React.ComponentType<{ className?: string }>; color: string }> = {
  pdf: { Comp: DocumentPdf24Filled, color: C.pdf },
  word: { Comp: DocumentText24Filled, color: C.word },
  excel: { Comp: DocumentTable24Filled, color: C.excel },
  powerpoint: { Comp: SlideText24Filled, color: C.powerpoint },
  image: { Comp: Image24Filled, color: C.image },
  archive: { Comp: FolderZip24Filled, color: C.archive },
  mail: { Comp: Mail24Filled, color: C.mail },
  video: { Comp: VideoClip24Filled, color: C.media },
  audio: { Comp: MusicNote224Filled, color: C.media },
  text: { Comp: DocumentText24Filled, color: C.text },
  other: { Comp: Document24Filled, color: C.other },
};

/** Farbiges Icon für eine Datei. */
export const FileIcon: React.FC<{ fileName: string; mimeType?: string | null; size?: number }> = ({
  fileName,
  mimeType,
  size = 20,
}) => {
  const { Comp, color } = ICON_BY_KIND[fileKind(fileName, mimeType)];
  return (
    <span className="docx-ic" style={{ color, width: size, height: size }} aria-hidden>
      <Comp />
    </span>
  );
};

/** Gelbes Ordner-Icon. */
export const FolderIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <span className="docx-ic" style={{ color: C.folder, width: size, height: size }} aria-hidden>
    <Folder24Filled />
  </span>
);

export { FILE_TYPE_COLORS } from "../dateiTyp";
